<template>
    <div :class="[backgroundClass, borderTopClass, borderBottomClass]">
        <div
            class="flex flex-row items-center font-light text-gray-700 gap-2 pl-2 pr-1 shrink-0 text-[0.8125rem]/none"
            :class="[borderRightClass, hasSlotContent ? 'py-0.5' : 'py-px']"
            :style="minHeightStyle"
            @mouseover="hoverTimelinePart(props.rowId, null, false)"
        >
            <!-- Copy-to-locations selection checkbox: shown while an allocation's edit popup is open. -->
            <div
                v-if="selectionCheckbox.status !== 'No-Selection'"
                class="shrink-0 flex items-center justify-center size-4.5"
            >
                <Tooltip>
                    <template #trigger="tooltipProps">
                        <input
                            ref="selectionCheckboxRef"
                            :aria-describedby="tooltipProps['aria-labelledby']"
                            :popovertarget="tooltipProps['popovertarget']"
                            :checked="isSelectionChecked"
                            :aria-disabled="isSelectionDisabled"
                            type="checkbox"
                            class="size-4.5 rounded-sm border-gray-300 text-sky-700 cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:z-40 focus:ring-gray-700 hover:border-gray-700 aria-disabled:focus-visible:ring-gray-500 aria-disabled:cursor-not-allowed aria-disabled:border-gray-300 aria-disabled:hover:border-gray-300"
                            :class="[
                                {
                                    'aria-disabled:bg-gray-400': selectionCheckbox.status === 'Disabled-Selected',
                                    'aria-disabled:bg-gray-200': selectionCheckbox.status === 'Disabled-Not-Selected',
                                },
                            ]"
                            @click="handleSelectionClick"
                            @change="handleSelectionToggle"
                        />
                    </template>

                    <template #text>{{ selectionCheckboxTooltip }}</template>
                </Tooltip>
            </div>

            <div v-else class="shrink-0 flex items-center justify-center size-4.5">
                <Tooltip>
                    <template #trigger="tooltipProps">
                        <AirplaneIcon
                            class="size-4.5 rounded-xs cursor-help focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:z-40 focus-visible:outline-gray-700"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        />
                    </template>

                    <template #text>
                        <div class="w-max">{{ $t('external-event') }}</div>
                    </template>
                </Tooltip>
            </div>

            <div class="flex flex-row items-center gap-1 min-w-max">
                <Tooltip :is-disabled="props.allocationCount.total === 0">
                    <template #trigger="tooltipProps">
                        <span
                            class="focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs"
                            :class="{ 'font-semibold': props.allocationCount.total > 0 }"
                            :aria-describedby="
                                props.allocationCount.total === 0 ? undefined : tooltipProps['aria-labelledby']
                            "
                            :popovertarget="props.allocationCount.total === 0 ? undefined : tooltipProps.popovertarget"
                            :tabindex="props.allocationCount.total === 0 ? undefined : 0"
                        >
                            {{ props.label }}
                        </span>

                        <span
                            v-if="props.allocationCount.total > 0"
                            class="font-light tabular-nums text-gray-500 ml-[0.5ch]"
                        >
                            <template v-if="props.allocationCount.inRange === props.allocationCount.total">
                                ({{ props.allocationCount.total }})
                            </template>

                            <template v-if="props.allocationCount.inRange !== props.allocationCount.total">
                                ({{ props.allocationCount.inRange }}/{{ props.allocationCount.total }})
                            </template>
                        </span>
                    </template>

                    <template #text>
                        <i18n
                            v-if="props.allocationCount.inRange === props.allocationCount.total"
                            path="location-contains-all-allocations"
                            :args="{ count: props.allocationCount.total }"
                        >
                            <template #allocationCount>
                                <span class="font-semibold">
                                    {{ props.allocationCount.total }}
                                </span>
                            </template>
                        </i18n>

                        <i18n
                            v-if="props.allocationCount.inRange !== props.allocationCount.total"
                            path="location-contains-allocations"
                            :args="{ count: props.allocationCount.total }"
                        >
                            <template #inRangeAllocationCount>
                                <span class="font-semibold">
                                    {{ props.allocationCount.inRange }}
                                </span>
                            </template>

                            <template #totalAllocationCount>
                                <span class="font-semibold">
                                    {{ props.allocationCount.total }}
                                </span>
                            </template>
                        </i18n>
                    </template>
                </Tooltip>
            </div>

            <div v-if="hasSlotContent" class="flex-1">
                <slot />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import AirplaneIcon from '@/components/Icon/AirplaneIcon.vue';
import { computed, inject, onMounted, onUnmounted, ref, useSlots, watchEffect } from 'vue';
import Tooltip from '../../Tooltip/Tooltip.vue';
import { useHeightSync } from '../composables/useHeightSync.ts';
import { hoveredTimelinePartsKey, hoverTimelinePartKey } from '../injectionKeys';
import { type CheckboxState, useLocationSelectionStore } from '../LocationSelection.store';

type ExternalEventFirstColumnCellProps = {
    rowId: string;
    /**
     * The external-event location this row represents. Present for real location rows (copy-to-locations
     * selection targets); omitted for aggregation/summary rows, which don't participate in selection.
     */
    locationId?: string;
    allocationCount: {
        inRange: number;
        total: number;
    };
    label: string;
};

const props = defineProps<ExternalEventFirstColumnCellProps>();

const locationSelectionStore = useLocationSelectionStore();

// Copy-to-locations selection (shared with the project/event tabs). Register so an allocation's edit popup
// can activate selection with this location, and so this row can be picked as a copy target. External-event
// locations are flat, so there is no parent. Aggregation rows have no `locationId` and don't participate.
onMounted(() => {
    if (props.locationId) {
        locationSelectionStore.registerLocation(props.locationId, null, 'ExternalLocation');
    }
});
onUnmounted(() => {
    if (props.locationId) {
        locationSelectionStore.unregisterLocation(props.locationId);
    }
});

const selectionCheckbox = computed<CheckboxState>(() => {
    if (!props.locationId) {
        return { status: 'No-Selection' };
    }
    return locationSelectionStore.getLocationCheckbox(props.locationId);
});
const selectionCheckboxTooltip = computed(() =>
    selectionCheckbox.value.status === 'No-Selection' ? '' : selectionCheckbox.value.tooltip,
);
const isSelectionDisabled = computed(
    () =>
        selectionCheckbox.value.status === 'Disabled-Selected' ||
        selectionCheckbox.value.status === 'Disabled-Not-Selected',
);
const isSelectionChecked = computed(
    () => selectionCheckbox.value.status === 'Selected' || selectionCheckbox.value.status === 'Disabled-Selected',
);

const selectionCheckboxRef = ref<HTMLInputElement | null>(null);
watchEffect(() => {
    if (selectionCheckboxRef.value) {
        selectionCheckboxRef.value.indeterminate = selectionCheckbox.value.status === 'Partially-Selected';
    }
});

function handleSelectionClick(event: MouseEvent) {
    // Prevent toggle for disabled items, stop propagation for all.
    event.stopPropagation();
    if (isSelectionDisabled.value) {
        event.preventDefault();
    }
}

function handleSelectionToggle() {
    if (props.locationId && !isSelectionDisabled.value) {
        locationSelectionStore.toggleLocationSelection(props.locationId);
    }
}

const hoveredTimelineParts = inject(hoveredTimelinePartsKey);
if (!hoveredTimelineParts) {
    throw new Error('ExternalEventFirstColumnCell must be used within a Timeline component');
}

const hoverTimelinePart = inject(hoverTimelinePartKey);
if (!hoverTimelinePart) {
    throw new Error('ExternalEventFirstColumnCell must be used within a Timeline component');
}

const isRowHovered = computed(() => {
    return hoveredTimelineParts.rowId.value === props.rowId;
});

const backgroundClass = computed(() => {
    if (isRowHovered.value) {
        if (hoveredTimelineParts.isConflictCell.value) {
            return 'bg-red-100';
        }

        return 'bg-blue-100';
    }

    return 'bg-gray-50';
});

const borderTopClass = computed(() => {
    if (isRowHovered.value) {
        if (hoveredTimelineParts.isConflictCell.value) {
            return 'first-column-cell-border-top first-column-cell-border-top-1 first-column-cell-border-top-red-200';
        }

        return 'first-column-cell-border-top first-column-cell-border-top-1 first-column-cell-border-top-blue-200';
    }
});

const borderBottomClass = computed(() => {
    if (isRowHovered.value) {
        if (hoveredTimelineParts.isConflictCell.value) {
            return 'first-column-cell-border-bottom first-column-cell-border-bottom-1 first-column-cell-border-bottom-red-200';
        }

        return 'first-column-cell-border-bottom first-column-cell-border-bottom-1 first-column-cell-border-bottom-blue-200';
    }

    return 'first-column-cell-border-bottom first-column-cell-border-bottom-1 first-column-cell-border-bottom-gray-200';
});

const borderRightClass = computed(() => {
    if (isRowHovered.value) {
        return 'first-column-cell-border-right first-column-cell-border-right-2-max-height first-column-cell-border-right-blue-400';
    }
});

const slots = useSlots();
const hasSlotContent = computed(() => slots.default?.().some((vnode) => typeof vnode.type !== 'symbol') ?? false);

const { heightInRem } = useHeightSync(props.rowId, hasSlotContent.value ? 1.5 : 1.25);
const minHeightStyle = computed(() => {
    return {
        minHeight: `${heightInRem.value}rem`,
    };
});
</script>

<style scoped>
/**
 * These are needed to avoid ugly triangular intersections of 1px and 2px wide
 * borders which can happen due to border-collapse: separate; being used.
 * border-collapse: separate; is required to implement sticky columns/rows
 * without losing the border when cells are scrolled under the sticky ones.
 */

.first-column-cell-border-top,
.first-column-cell-border-bottom,
.first-column-cell-border-right {
    position: relative;
}

.first-column-cell-border-top-1::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 100%;
    height: 1px;
    z-index: 10;
}
.first-column-cell-border-top-red-200::before {
    background-color: var(--color-red-200);
}
.first-column-cell-border-top-blue-200::before {
    background-color: var(--color-blue-200);
}

.first-column-cell-border-bottom-1::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 1px;
}
.first-column-cell-border-bottom-red-200::after {
    background-color: var(--color-red-200);
}
.first-column-cell-border-bottom-blue-200::after {
    background-color: var(--color-blue-200);
}
.first-column-cell-border-bottom-gray-200::after {
    background-color: var(--color-gray-200);
}

.first-column-cell-border-right-2::after {
    content: '';
    position: absolute;
    top: 0;
    right: -2px;
    width: 2px;
    height: calc(100%);
}
.first-column-cell-border-right-2-max-height::after {
    content: '';
    position: absolute;
    top: -1px;
    right: -2px;
    width: 2px;
    height: calc(100% + 2px);
}
.first-column-cell-border-right-blue-400::after {
    background-color: var(--color-blue-400);
}
</style>
